import React, { useState, useEffect } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Button, TextField, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Typography, Collapse, Divider, useMediaQuery, useTheme,
  Tooltip, CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Print from "@mui/icons-material/Print";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { getAllEnquiries, deleteEnquiry, getUnits, getDivisionUnits } from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handlePrint } from "./EnquiryPrint";

let enquiryMapRef = {};

const EnquiryList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [expandedEnquiry, setExpandedEnquiry] = useState(null);
  const [units, setUnits] = useState([]); // ✅ State to store units list

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ✅ Get division from localStorage
  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const storedDivision = getSelectedDivision();
      const divisionId = storedDivision?.division_id || "";
      
      // ✅ Fetch enquiries and units list simultaneously
      const [data, unitRes] = await Promise.all([
        getAllEnquiries(divisionId),
        getDivisionUnits().catch(() => getUnits())
      ]);

      const list = Array.isArray(data) ? data : data?.results || data?.data || [];
      setEnquiries(list);

      // Save units to state
      const uList = unitRes?.units || unitRes?.results || unitRes || [];
      setUnits(Array.isArray(uList) ? uList : []);
    } catch (err) {
      console.error("Fetch data error", err);
      toast.error("Failed to load data");
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const confirmDelete = async () => {
    if (!enquiryToDelete?.enquiry_id) {
      toast.error("Invalid ID");
      return;
    }
    const response = await deleteEnquiry(enquiryToDelete.enquiry_id);
    if (response !== false) {
      setEnquiries((prev) => prev.filter((e) => e.enquiry_id !== enquiryToDelete.enquiry_id));
      toast.success("Enquiry deleted successfully!");
    } else {
      toast.error("Failed to delete enquiry");
    }
    setDeleteDialogOpen(false);
    setEnquiryToDelete(null);
  };

  // ✅ Search filter
  const filteredEnquiries = enquiries.filter((enq) => {
    const custMatch =
      (enq.customer_name || "").toLowerCase().includes(customerSearch.toLowerCase()) ||
      (enq.enquiry_no || "").toLowerCase().includes(customerSearch.toLowerCase()) ||
      (enq.phone || "").includes(customerSearch);
    const compMatch = (enq.company_name || "").toLowerCase().includes(companySearch.toLowerCase());
    return custMatch && compMatch;
  });

  const handleDeleteClick = (enquiry) => {
    setEnquiryToDelete(enquiry);
    setDeleteDialogOpen(true);
  };

  const truncateText = (text, limit = 18) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "…" : text;
  };

  // ── Compact Minimal row style (Plain Black & White) ──
  const rs = { "& td": { fontSize: "0.72rem", py: 0.4, px: 0.8 } };
  const hs = { fontWeight: 600, fontSize: "0.73rem", py: 0.6, px: 0.8, bgcolor: "#f0f0f0", color: "#333", borderBottom: "1px solid #ccc" };

  return (
    <Box sx={{ px: 0, pt: 0, mt: { xs: 3, md: -3 }, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Search Header */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search Ref / Enq / Party / Phone"
              size="small"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              sx={{ "& .MuiInputBase-root": { height: 32 } }}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search Company"
              size="small"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              sx={{ "& .MuiInputBase-root": { height: 32 } }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/dashboard/Createenquiry")}
              startIcon={<AddIcon />}
              sx={{ height: 32, fontSize: "0.75rem", bgcolor: "#0c96f8", "&:hover": { bgcolor: "#555" } }}
            >
              Add Enquiry
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Loading Spinner */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isMobile ? (
        /* Mobile Cards View (Minimal B&W Style) */
        <Box sx={{ bgcolor: "#fff" }}>
          {filteredEnquiries.length === 0 && (
            <Typography sx={{ p: 2, textAlign: "center", color: "#999", fontSize: "0.8rem" }}>
              No Enquiry Found
            </Typography>
          )}
          {filteredEnquiries.map((e, index) => (
            <Box key={e.enquiry_id || index}>
              <Box
                onClick={() => setExpandedEnquiry(expandedEnquiry === e.enquiry_id ? null : e.enquiry_id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1,
                  cursor: "pointer",
                  bgcolor: "#fff",
                  borderBottom: "1px solid #eee",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3 }}>
                    <Typography sx={{ fontSize: "0.65rem", color: "#999", minWidth: 20 }}>
                      #{index + 1}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>{e.enquiry_no || "ENQ-?"}</Typography>
                    <Typography sx={{ fontWeight: "bold", fontSize: "0.75rem", flex: 1 }}>
                      {e.customer_name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                      {e.status || "New"}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "0.65rem", color: "#888" }}>
                    {trunc(e.company_name, 25)} • {e.phone || "-"}
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ p: 0.3 }}>
                  {expandedEnquiry === e.enquiry_id ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>

              <Collapse in={expandedEnquiry === e.enquiry_id} unmountOnExit>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: "#fafafa", borderBottom: "1px solid #eee" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mb: 0.8 }}>
                    {[
                      ["ENQ NO", e.enquiry_no],
                      ["EMAIL", e.email],
                      ["CITY", e.city],
                      ["SOURCE", e.enquiry_source],
                      ["FOLLOW-UP", e.follow_up_date ? new Date(e.follow_up_date).toLocaleDateString("en-GB") : "-"]
                    ].map(([l, v]) => (
                      <Box key={l}>
                        <Typography sx={{ fontSize: "0.6rem", color: "#aaa", letterSpacing: 0.5 }}>{l}</Typography>
                        <Typography sx={{ fontSize: "0.68rem", fontWeight: 500 }}>{v || "-"}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        navigate("/dashboard/Createenquiry", { state: { data: e } });
                      }}
                      sx={{ width: 26, height: 26, border: "1px solid #ddd", color: "#1976d2" }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        handleDeleteClick(e);
                      }}
                      sx={{ width: 26, height: 26, border: "1px solid #ddd", color: "#d32f2f" }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        handlePrint(e, units); // ✅ Passed units list
                      }}
                      sx={{ width: 26, height: 26, border: "1px solid #ddd", color: "#333" }}
                    >
                      <Print sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>
              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* Desktop Table View (Compact Grayscale) */
        <TableContainer component={Paper} sx={{ maxHeight: "88vh", overflow: "auto", border: "1px solid #ddd" }} elevation={0}>
          <Table
            stickyHeader
            size="small"
            sx={{ minWidth: 1600, "& td, & th": { textAlign: "center", whiteSpace: "nowrap", borderBottom: "1px solid #ccc" } }}
          >
            <TableHead>
              <TableRow>
                {["S.No.", "Enquiry No", "Contact Person", "Company", "Email", "Phone", "City", "Country", "Enquiry Source", "Date", "Followup Date", "Status", "Action"].map((h, i) => (
                  <TableCell
                    key={h}
                    sx={{
                      ...hs,
                      ...(i === 0 ? { position: "sticky", left: 0, zIndex: 3 } : {}),
                      ...(i === 1 ? { position: "sticky", left: 45, zIndex: 3 } : {}),
                      ...(i === 12 ? { position: "sticky", right: 0, zIndex: 3 } : {}),
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3, color: "text.secondary", fontSize: "0.75rem" }}>
                    No Data Found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEnquiries.map((e, idx) => (
                  <TableRow
                    hover
                    key={e.enquiry_id || idx}
                    sx={{ ...rs, backgroundColor: idx % 2 === 0 ? "#fafafa" : "#ffffff", "&:hover": { backgroundColor: "#f9f9f9" } }}
                  >
                    <TableCell sx={{ position: "sticky", left: 0, backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff", zIndex: 1 }}>{idx + 1}</TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 45,
                        backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff",
                        zIndex: 1,
                        fontWeight: "bold",
                        color: "#1976d2",
                      }}
                    >
                      {e.enquiry_no || `ENQ-${e.enquiry_id}`}
                    </TableCell>
                    <TableCell>{e.customer_name || "-"}</TableCell>
                    <TableCell>{e.company_name || "-"}</TableCell>
                    <TableCell>
                      <Tooltip title={e.email || ""}>
                        <span>{truncateText(e.email, 20)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{e.phone || "-"}</TableCell>
                    <TableCell>{e.city || "-"}</TableCell>
                    <TableCell>{e.country || "India"}</TableCell>
                    <TableCell>{e.enquiry_source || "-"}</TableCell>
                    <TableCell>
                      {e.created_at ? new Date(e.created_at).toLocaleDateString("en-GB") : "-"}
                    </TableCell>
                    <TableCell>
                      {e.follow_up_date ? new Date(e.follow_up_date).toLocaleDateString("en-GB") : "-"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {e.status || "New"}
                    </TableCell>
                    <TableCell sx={{ position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => navigate("/dashboard/Createenquiry", { state: { data: e } })}
                        color="primary"
                        sx={{ p: 0.3 }}
                      >
                        <EditIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(e)} color="error" sx={{ p: 0.3 }}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handlePrint(e, units)} color="info" sx={{ p: 0.3 }}>
                        <Print sx={{ fontSize: 15 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirm Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontSize: "0.9rem", py: 1.5 }}>Confirm Delete</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography sx={{ fontSize: "0.8rem" }}>
            Delete enquiry <b>{enquiryToDelete?.enquiry_no}</b> ({enquiryToDelete?.customer_name})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ py: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small">Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} size="small" sx={{ bgcolor: "#333", "&:hover": { bgcolor: "#555" } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Simple trunc helper for clean list view
const trunc = (t, l = 18) => (!t ? "" : t.length > l ? t.substring(0, l) + "…" : t);

export default EnquiryList;